// pages/UsersPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Users, Search, Plus } from "lucide-react";
import { DataTable } from "@/components/ui/table";
import AddUserDialog from "./modals/AddUserDialog";
import { getUserList, saveUserInfo } from "@/redux/Masters/users/actions";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { formatDisplayDate, formatDisplayDateTime } from "@/utils/dateUtils";
import TableText from "@/components/common/TableText";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import PasswordCell from "@/components/common/PasswordCell";
import TablePagination from "@/components/common/TablePagination";
import { callLoader } from "@/redux/Loader/actions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const UsersPage = () => {
  const dispatch = useDispatch();

  const { usersList, usersPagination } = useSelector((state) => state.users);
  console.log("usersPagination: ", usersPagination);
  const { roleList } = useSelector((state) => state.common);

  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const [profileImageFile, setProfileImageFile] = useState(null);

  // User form
  const [userForm, setUserForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "",
    contact_no: "",
    company_id: "",
    company_name: "",
    role_id: "",
    office_id: "",
    profit_center: "",
    profit_type: "",
    profit: "",
    salesperson: false,
    is_active: true,
  });

  const columns = [
    {
      key: "user",
      label: "User",

      render: (row) => {
        const name =
          `${row.FIRSTNAME || ""} ${row.LASTNAME || ""}`.trim() || "User";

        const profileImage = row.PROFILEIMAGE;

        const isValidImage =
          profileImage &&
          profileImage !== "https://api.shipez.ai/" &&
          profileImage.startsWith("http");

        const initials =
          (row.FIRSTNAME?.charAt(0) || "") + (row.LASTNAME?.charAt(0) || "");

        return (
          <div className="group flex items-center gap-3">
            {/* AVATAR */}
            <Avatar className="h-11 w-11 rounded-xl     ">
              <AvatarImage
                src={isValidImage ? profileImage : undefined}
                alt={name}
                className="object-cover"
              />

              <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white font-semibold">
                {initials || "U"}
              </AvatarFallback>
            </Avatar>

            {/* INFO */}
            <div className="min-w-0">
              <TableText
                title={name}
                titleClassName="
              truncate font-semibold
              group-hover:text-teal-500
              transition-colors
              dark:text-white
            "
                subtitleClassName="truncate"
              />
            </div>
          </div>
        );
      },
    },

    {
      key: "username",
      label: "Username",

      render: (row) => {
        const username = row.USERNAME || "";
        return <TableText title={username ? `${username}` : "-"} />;
      },
    },

    {
      key: "password",
      label: "Password",

      render: (row) => {
        const password = row.PASSWORD || "";
        return <PasswordCell password={password} />;
      },
    },
    {
      key: "email",
      label: "Email",
      render: (row) => <TableText title={row.EMAILID} />,
    },

    {
      key: "contact",
      label: "Contact",

      render: (row) => <TableText title={row.CONTACTNO} />,
    },

    {
      key: "role",
      label: "Role",

      render: (row) => (
        <TableText
          title={row.ROLETYPE || "-"}
          subtitle={`Role ID: ${row.ROLEID}`}
          titleClassName="
          inline-flex w-fit rounded-lg
          bg-violet-50 px-2.5 py-1
          text-xs font-semibold text-violet-700
          dark:bg-violet-900/20
          dark:text-violet-300
        "
        />
      ),
    },

    {
      key: "office",
      label: "Office",
      render: (row) => <TableText title={row.OFFICETYPENAME} />,
    },

    {
      key: "profit",
      label: "Profit",

      render: (row) => (
        <TableText
          title={`${row.PROFIT}%`}
          subtitle={row.PROFITTYPE}
          titleClassName="
          font-semibold
          text-emerald-600
          dark:text-emerald-400
        "
        />
      ),
    },

    {
      key: "permissions",
      label: "Permissions",

      render: (row) => (
        <div className="flex flex-wrap gap-1.5">
          {row.IS_SALESPERSON && (
            <span
              className="
              rounded-full
              bg-blue-50 px-2.5 py-1
              text-[11px] font-medium text-blue-700
              dark:bg-blue-900/20
              dark:text-blue-300
            "
            >
              Sales
            </span>
          )}

          {row.IS_FILEMANAGER && (
            <span
              className="
              rounded-full
              bg-amber-50 px-2.5 py-1
              text-[11px] font-medium text-amber-700
              dark:bg-amber-900/20
              dark:text-amber-300
            "
            >
              File Manager
            </span>
          )}

          {!row.IS_SALESPERSON && !row.IS_FILEMANAGER && (
            <span
              className="
                text-xs text-slate-400
                dark:text-slate-500
              "
            >
              —
            </span>
          )}
        </div>
      ),
    },

    {
      key: "created",
      label: "Created On",

      render: (row) => (
        <TableText
          title={formatDisplayDate(row.CREATED_ON)}
          subtitle={`By ${row.CREATED_BY || "-"}`}
        />
      ),
    },

    {
      key: "status",
      label: "Status",

      render: (row) => (
        <span
          className={`
          inline-flex items-center gap-1.5
          rounded-full px-2.5 py-1
          text-xs font-semibold

          ${
            row.ISACTIVE
              ? `
                bg-emerald-50
                text-emerald-700
                dark:bg-emerald-900/20
                dark:text-emerald-300
              `
              : `
                bg-red-50
                text-red-700
                dark:bg-red-900/20
                dark:text-red-300
              `
          }
        `}
        >
          {row.ISACTIVE ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    columns.map((col) => col.key),
  );

  // FILTERED COLUMNS
  const filteredColumns = useMemo(() => {
    return columns.filter((col) => visibleColumns.includes(col.key));
  }, [visibleColumns]);

  useEffect(() => {
    fetchUsersList();
  }, [currentPage, pageSize, debouncedSearch]);

  const totalPages = usersPagination?.totalPage || 1;

  const totalRecordCount = usersPagination?.totalRecord ?? 0;

  const fetchUsersList = async (
    page = currentPage,
    search = debouncedSearch,
    pageLimit = pageSize,
  ) => {
    try {
      setLoading(true);

      const requestPayload = {
        iPageNo: page,
        iPageRecords: pageLimit,
        SearchKeyword: search,
        // CompanyId: 3,
      };

      const response = dispatch(getUserList(requestPayload));

      console.log("response: ", response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Create User
  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      // ───────────────── VALIDATION ─────────────────

      const requiredFields = [
        { key: "first_name", msg: "First name is required" },
        { key: "last_name", msg: "Last name is required" },
        { key: "email", msg: "Email is required" },
        { key: "username", msg: "Username is required" },
        { key: "password", msg: "Password is required" },
      ];

      for (let field of requiredFields) {
        if (!userForm?.[field.key]?.trim()) {
          return toast.error(field.msg);
        }
      }

      if (userForm.password.length < 8) {
        return toast.error("Password must be at least 8 characters");
      }

      if (!userForm?.company_id) {
        return toast.error("Company is required");
      }

      if (!userForm?.role_id) {
        return toast.error("Role is required");
      }

      setModalLoading(true);

      // ───────────────── FORM DATA ─────────────────

      const formData = new FormData();

      // 🔥 MAP YOUR FRONTEND → BACKEND KEYS
      formData.append("FIRSTNAME", userForm.first_name);
      formData.append("LASTNAME", userForm.last_name);
      formData.append("EMAILID", userForm.email);
      formData.append("USERNAME", userForm.username);
      formData.append("PASSWORD", userForm.password);
      formData.append("CONTACTNO", userForm.contact_no || "");
      formData.append("COMPANYID", userForm.company_id);
      formData.append("ROLEID", userForm.role_id);
      formData.append("OFFICEID", userForm.office_id || 0);
      formData.append("PROFIT", userForm.profit || 0);
      formData.append("PROFITTYPE", userForm.profit_type || "");
      formData.append("COMPANY_NAME", userForm.company_name || "");

      formData.append("IS_SALESPERSON", userForm.salesperson ?? false);
      formData.append("ISACTIVE", userForm.is_active ?? true);
      formData.append("IS_FILEMANAGER", false); // adjust if needed
      formData.append("CREATED_BY", "super_admin");

      // ───────────────── IMAGE ─────────────────

      if (profileImageFile instanceof File) {
        formData.append(
          "PROFILE_IMAGE",
          profileImageFile,
          profileImageFile.name,
        );
      }

      // ───────────────── API ─────────────────

      const response = await dispatch(saveUserInfo(formData));

      const success =
        response?.statuscode === 200 || response?.statuscode === 201;

      if (success) {
        toast.success("User created successfully");

        setUserForm({
          first_name: "",
          last_name: "",
          email: "",
          username: "",
          password: "",
          contact_no: "",
          company_id: "",
          company_name: "",
          role_id: "",
          office_id: "",
          profit_center: "",
          profit_type: "",
          profit: "",
          salesperson: false,
          is_active: true,
        });

        setProfileImageFile(null);

        setShowUserModal(false);
      } else {
        toast.error(response?.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Create User Error:", error);
      toast.error(error?.message || "Something went wrong");
    } finally {
      setModalLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();

    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Users className="w-7 h-7 text-teal-500" />
              Users
            </h1>

            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage users, roles and company access
            </p>
          </div>

          <button
            onClick={() => {
              setModalError("");
              setShowUserModal(true);
            }}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative w-full md:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
              <span className="font-bold text-slate-500 dark:text-white">
                {usersList?.length}
              </span>{" "}
              users found
            </div>
          </div>

          <ColumnVisibilityDropdown
            columns={columns}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            storageKey="master-user-table-columns"
          />
        </div>

        <DataTable
          data={usersList}
          columns={filteredColumns}
          loading={loading}
          emptyText="No Users found"
          Icon={Users}
        />
        <div className="p-4">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecordCount}
            currentRecord={usersPagination?.currentRecord}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserDialog
        open={showUserModal}
        onOpenChange={setShowUserModal}
        profileImageFile={profileImageFile}
        setProfileImageFile={setProfileImageFile}
        modalError={modalError}
        handleCreateUser={handleCreateUser}
        userForm={userForm}
        setUserForm={setUserForm}
        companies={companies}
        roles={roleList}
        modalLoading={modalLoading}
      />
      {/* {showUserModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-slate-900 rounded-xl border border-slate-700 p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-white">
                                Add New User
                            </h2>

                            <button
                                onClick={() => setShowUserModal(false)}
                                className="p-1 hover:bg-slate-700 rounded"
                            >
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>

                        {modalError && (
                            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                                {modalError}
                            </div>
                        )}

                        <form
                            onSubmit={handleCreateUser}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">
                                    Company *
                                </label>

                                <select
                                    value={userForm.company_id}
                                    onChange={(e) =>
                                        setUserForm({
                                            ...userForm,
                                            company_id: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    required
                                >
                                    <option value="">
                                        Select Company
                                    </option>

                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.company_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">
                                        First Name *
                                    </label>

                                    <input
                                        type="text"
                                        value={userForm.first_name}
                                        onChange={(e) =>
                                            setUserForm({
                                                ...userForm,
                                                first_name: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="John"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">
                                        Last Name *
                                    </label>

                                    <input
                                        type="text"
                                        value={userForm.last_name}
                                        onChange={(e) =>
                                            setUserForm({
                                                ...userForm,
                                                last_name: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Doe"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-slate-400 mb-1">
                                    Email *
                                </label>

                                <input
                                    type="email"
                                    value={userForm.email}
                                    onChange={(e) =>
                                        setUserForm({
                                            ...userForm,
                                            email: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    placeholder="john@company.com"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-slate-400 mb-1">
                                    Password *
                                </label>

                                <input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(e) =>
                                        setUserForm({
                                            ...userForm,
                                            password: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    placeholder="Min 8 characters"
                                    required
                                    minLength={8}
                                />
                            </div>



                            <div>
                                <label className="block text-sm text-slate-400 mb-1">
                                    Role *
                                </label>

                                <select
                                    value={userForm.role_id}
                                    onChange={(e) =>
                                        setUserForm({
                                            ...userForm,
                                            role_id: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    required
                                >
                                    <option value="">Select Role</option>

                                    {roles.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.role_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowUserModal(false)
                                    }
                                    className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={modalLoading}
                                    className="flex-1 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {modalLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Plus className="w-4 h-4" />
                                    )}

                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )} */}
    </div>
  );
};

export default UsersPage;
